import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { Icon, Text } from '@mxenabled/mxui'

import {
  Dialog,
  Drawer,
  useBudgetsStore,
  useCategoryStore,
  useGlobalCopyStore,
} from '../../../common'
import { type DetailedBudget, type DetailedCategory } from '../../../core'
import { CategoryRow, useCategoryUiStore } from '../../../categories'
import { BudgetAmount } from '../shared/BudgetAmount'
import { findDetailedCategory, findDetailedSubCategory } from '../../../core/utils/CategoryUtil'

interface AddSubBudgetProps {
  category: DetailedCategory
  parentBudget: DetailedBudget
}

export const AddSubBudget: React.FC<AddSubBudgetProps> = observer(({ category, parentBudget }) => {
  const { budgets: copy, common: commonCopy } = useGlobalCopyStore()
  const { setExpandedGuid, setOnSelect } = useCategoryUiStore()
  const { addBudget } = useBudgetsStore()
  const { detailedCategories } = useCategoryStore()

  const [isOpen, setIsOpen] = React.useState(false)
  const [isAddBudgetOpen, setIsAddBudgetOpen] = React.useState(false)
  const [amount, setAmount] = React.useState('')
  const [subCategory, setSubCategory] = React.useState<DetailedCategory | undefined>(undefined)

  const catsRef = React.useRef(detailedCategories)
  catsRef.current = detailedCategories

  const currentCategory = findDetailedCategory(detailedCategories, category.guid) || category

  React.useEffect(() => {
    setOnSelect((guid: string) => {
      const cats = catsRef.current
      const selected =
        findDetailedCategory(cats, category.guid)?.subCategories.find((sc) => sc.guid === guid) ||
        findDetailedSubCategory(cats, guid)
      if (selected) {
        setSubCategory(selected)
        setAmount(`${selected.currentAmount === 0 ? '' : selected.currentAmount}`)
        setIsAddBudgetOpen(true)
      }
    })
  }, [category.guid])

  const handleOpen = () => {
    setExpandedGuid(currentCategory.guid)
    setIsOpen(true)
  }

  const handleAddSubBudget = async () => {
    if (subCategory && Number(amount) > 0) {
      await addBudget(subCategory, Number(amount), parentBudget.guid)
      setIsAddBudgetOpen(false)
      setIsOpen(false)
      setSubCategory(undefined)
    }
  }

  return (
    <Box>
      <ListItem disableGutters={true} disablePadding={true} sx={{ bgcolor: 'background.paper' }}>
        <ListItemButton onClick={handleOpen}>
          <ListItemText>
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Stack alignItems="center" direction="row" gap={8}>
                <Icon fill={true} name="add_box" size={24} sx={{ color: 'primary.main' }} />
                <Text bold={true} sx={{ color: 'primary.main' }} variant="body1">
                  {copy.add_sub_budget_button}
                </Text>
              </Stack>
              <Icon name="chevron_right" size={24} sx={{ color: 'primary.main' }} />
            </Stack>
          </ListItemText>
        </ListItemButton>
      </ListItem>
      <Divider />

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={copy.add_sub_budget_title}
      >
        <List>
          <CategoryRow category={currentCategory} subCategories={currentCategory.subCategories} />
        </List>
      </Drawer>

      <Dialog
        copy={{ close_aria: commonCopy.close_aria, title: copy.add_title }}
        disablePrimaryButton={Number(amount) <= 0}
        isOpen={isAddBudgetOpen}
        onClose={() => setIsAddBudgetOpen(false)}
        onPrimaryAction={handleAddSubBudget}
        primaryText={copy.add_save_button}
      >
        <BudgetAmount
          amount={amount}
          categoryName={subCategory?.name || ''}
          onAmountChanged={setAmount}
        />
      </Dialog>
    </Box>
  )
})
